import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const FILTERS = ["All", "Automatic", "Manual", "Wholesale", "Quantity"];

const empty = {
  name: "", type: "Automatic", sub_type: "Wholesale",
  discount_percent: 0, customer_group: "",
  tiers: [], status: "Active",
};

const DEFAULT_TIERS = [
  { name: "Bronze", discount_percent: 5 },
  { name: "Silver", discount_percent: 10 },
  { name: "Gold", discount_percent: 20 },
];

export default function PricingRules() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (["Automatic", "Manual"].includes(filter)) params.type = filter;
    if (["Wholesale", "Quantity"].includes(filter)) params.sub_type = filter;
    const r = await api.get("/pricing-rules", { params });
    setRows(r.data);
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/pricing-rules", form);
      toast.success("Pricing rule created");
      setOpen(false);
      setForm(empty);
      load();
    } catch {
      toast.error("Failed to create rule");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this pricing rule?")) return;
    await api.delete(`/pricing-rules/${id}`);
    toast.success("Rule deleted");
    load();
  }

  async function toggle(rule) {
    const status = rule.status === "Active" ? "Inactive" : "Active";
    await api.patch(`/pricing-rules/${rule.id}`, { status });
    load();
  }

  function addTier() {
    setForm({
      ...form,
      tiers: [...form.tiers, { _key: crypto.randomUUID(), name: "", discount_percent: 0 }],
    });
  }
  function updateTier(i, k, v) {
    const t = [...form.tiers];
    t[i] = { ...t[i], [k]: k === "discount_percent" ? Number(v) : v };
    setForm({ ...form, tiers: t });
  }
  function removeTier(i) {
    setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  }
  function useDefaultTiers() {
    setForm({
      ...form,
      tiers: DEFAULT_TIERS.map((t) => ({ ...t, _key: crypto.randomUUID() })),
    });
  }

  return (
    <div data-testid="pricing-rules-page">
      <PageHeader
        title="Pricing Rules"
        subtitle="Automatic and manual discounts for wholesale customers"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="pricing-create-btn">
            <Plus size={14} /> Create pricing rule
          </button>
        }
      />

      <div className="b2b-card">
        <div className="flex border-b" style={{ borderColor: "var(--brand-border)" }} data-testid="pricing-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`b2b-tab ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`pricing-filter-${f.toLowerCase()}`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No pricing rules" subtitle="Create pricing rules to offer discounts to wholesalers." testId="pricing-empty" />
        ) : (
          <table className="b2b-table" data-testid="pricing-table">
            <thead>
              <tr>
                <th>Pricing rule</th>
                <th>Status</th>
                <th>Type</th>
                <th>Sub-type</th>
                <th>Discount</th>
                <th>Created at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`pricing-row-${r.id}`}>
                  <td className="font-medium">{r.name}</td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={r.status === "Active"} onChange={() => toggle(r)} data-testid={`pricing-toggle-${r.id}`} />
                      <span className="slider" />
                    </label>
                    <span className="ml-2 text-xs" style={{ color: "var(--brand-muted)" }}>{r.status}</span>
                  </td>
                  <td>{r.type}</td>
                  <td>{r.sub_type}</td>
                  <td>{r.tiers?.length ? `${r.tiers.length} tiers` : `${r.discount_percent}%`}</td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-danger" onClick={() => remove(r.id)} data-testid={`pricing-delete-${r.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create pricing rule"
        testId="pricing-create-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="pricing-create-cancel">Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="pricing-create-save">Create rule</button>
          </>
        }
      >
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="form-label">Rule name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pricing-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Type</label>
              <select className="form-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="pricing-form-type">
                <option>Automatic</option>
                <option>Manual</option>
              </select>
            </div>
            <div>
              <label className="form-label">Sub-type</label>
              <select className="form-select" value={form.sub_type} onChange={(e) => setForm({ ...form, sub_type: e.target.value })} data-testid="pricing-form-subtype">
                <option>Wholesale</option>
                <option>Quantity</option>
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Discount % off retail</label>
            <input type="number" min="0" max="100" className="form-input" value={form.discount_percent}
              onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })} data-testid="pricing-form-discount" />
          </div>
          <div>
            <label className="form-label">Apply to customer group</label>
            <input className="form-input" placeholder="e.g. Wholesaler" value={form.customer_group}
              onChange={(e) => setForm({ ...form, customer_group: e.target.value })} data-testid="pricing-form-group" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="form-label mb-0">Pricing tiers (optional)</label>
              <div className="flex gap-2">
                <button type="button" className="text-xs underline" onClick={useDefaultTiers} data-testid="pricing-form-default-tiers">
                  Use Bronze/Silver/Gold
                </button>
                <button type="button" className="text-xs underline" onClick={addTier} data-testid="pricing-form-add-tier">
                  + Add tier
                </button>
              </div>
            </div>
            {form.tiers.map((t, i) => (
              <div key={t._key || i} className="flex gap-2 mb-2" data-testid={`pricing-tier-row-${i}`}>
                <input className="form-input flex-1" placeholder="Tier name" value={t.name}
                  onChange={(e) => updateTier(i, "name", e.target.value)} data-testid={`pricing-tier-name-${i}`} />
                <input type="number" className="form-input w-28" placeholder="% off" value={t.discount_percent}
                  onChange={(e) => updateTier(i, "discount_percent", e.target.value)} data-testid={`pricing-tier-discount-${i}`} />
                <button type="button" onClick={() => removeTier(i)} className="btn-danger" data-testid={`pricing-tier-remove-${i}`}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </form>
      </Modal>
    </div>
  );
}
